import kebabCase from 'lodash/kebabCase';

// Valid placement identifiers. GTM/QM decide which contexts to collect.
export const analyticsContexts = {
	// Events landing page
	'events-header': 'Header View all Events link.',
	'events-featured-card': 'Featured promo cards.',
	'events-audience-shelf': 'Audience/program carousel (unlisted fallback).',
	'events-audience-shelf-adults': 'Adults carousel.',
	'events-audience-shelf-families-teens': 'Families, Kids, and Teens carousel.',
	'events-audience-shelf-members': 'Members carousel.',
	'events-audience-shelf-met-live-arts': 'Met Live Arts carousel.',
	'events-calendar-promo': 'Calendar promo.',
} as const;

export type AnalyticsContext = keyof typeof analyticsContexts;

export const getEventsAudienceShelfContext = (
	aceAudience?: string,
	aceProgram?: string,
): AnalyticsContext => {
	const context = `events-audience-shelf-${kebabCase(aceAudience || aceProgram)}`;
	return Object.keys(analyticsContexts).includes(context)
		? (context as AnalyticsContext)
		: 'events-audience-shelf';
};

export const getContextAttributes = (context?: AnalyticsContext) =>
	context ? { 'data-analytics-context': context } : undefined;

type ContentAnalyticsAttributes = {
	'data-analytics-content-id'?: string | number;
	'data-analytics-content-name'?: string;
};

export type NavigationAnalyticsAttributes = ContentAnalyticsAttributes & {
	'data-analytics-event': 'navigate';
	// For navigation buttons without an href; normal links use their own href.
	'data-analytics-destination'?: string;
};

export type DownloadAnalyticsAttributes = ContentAnalyticsAttributes & {
	'data-analytics-event': 'download';
	'data-analytics-download-type': 'pdf' | 'image' | '3d_model';
	'data-analytics-destination'?: string;
	'data-analytics-resource-locale'?: string;
};

export type MapAnalyticsAttributes = ContentAnalyticsAttributes & {
	'data-analytics-event': 'open_map';
	'data-analytics-destination'?: string;
};

export type View3DAnalyticsAttributes = ContentAnalyticsAttributes & {
	'data-analytics-event': 'view_3d';
};

export type InteractionAnalyticsAttributes =
	| NavigationAnalyticsAttributes
	| DownloadAnalyticsAttributes
	| MapAnalyticsAttributes
	| View3DAnalyticsAttributes;

type AnalyticsAction = {
	contentId?: string | number;
	contentName?: string;
} & (
	| { event?: 'navigate'; destination?: string }
	| { event: 'open_map'; destination?: string }
	| {
			event: 'download';
			downloadType: DownloadAnalyticsAttributes['data-analytics-download-type'];
			destination?: string;
			resourceLocale?: string;
	  }
	| { event: 'view_3d' }
);

// Controls describe themselves even when rendered outside a tracked context.
export const getActionAttributes = (
	action: AnalyticsAction,
): InteractionAnalyticsAttributes | undefined => {
	const { event } = action;
	const content = {
		'data-analytics-content-id': action.contentId,
		'data-analytics-content-name': action.contentName?.trim() || undefined,
	};

	switch (event) {
		case undefined:
		case 'navigate':
		case 'open_map':
			return {
				...content,
				'data-analytics-event': event ?? 'navigate',
				'data-analytics-destination': action.destination,
			};
		case 'download':
			return {
				...content,
				'data-analytics-event': event,
				'data-analytics-download-type': action.downloadType,
				'data-analytics-destination': action.destination,
				'data-analytics-resource-locale': action.resourceLocale,
			};
		case 'view_3d':
			return { ...content, 'data-analytics-event': event };
		default: {
			const unexpectedEvent: never = event;
			console.warn('Unknown analytics event:', unexpectedEvent);
			return undefined;
		}
	}
};
